import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { format, parseISO } from "date-fns";
import type { ScheduleItem, Traveler, TravelerId, TripData } from "../types";
import {
  periodLabel,
  sameSchedulePeriod,
  scheduleSortKey,
  sortScheduleItems,
  type SchedulePeriod,
} from "../lib/sortSchedule";
import type { TripStore } from "../hooks/useTripData";
import { CategoryIcon, StatusBadge } from "./StatusBadge";
import { Fragment, useState } from "react";
import { PlaceDetail } from "./PlaceDetail";
import { FeedPhotoStrip } from "./FeedPhotoStrip";
import { findPinForScheduleItem } from "./PlacePhoto";
import { pinMayHavePhoto } from "../lib/googlePlaces";
import { formatEventHours } from "../lib/formatEventHours";
import { hopLineAt, type LatLng } from "../lib/hop";
import { itemVisitStates, type VisitState } from "../lib/questVisit";
import { VoteButtons } from "./VoteButtons";
import { chipsForScheduleItem } from "../lib/travelerChips";
import { soloInitial, soloTravelerForItem } from "../lib/soloAvatar";

interface Props {
  date: string;
  items: ScheduleItem[];
  store: TripStore;
  travelers: Traveler[];
  allPins: TripData["pins"];
  dayPins: TripData["pins"];
  voterId: TravelerId;
  /** Selected quest. When unset, the first item of the day is active. */
  selectedItemId?: string | null;
  onSelectItem?: (id: string) => void;
}

/** Only unconfirmed food / coffee / attraction items can be reordered. */
export function isDraggable(
  item: Pick<ScheduleItem, "category" | "status">,
): boolean {
  return (
    (item.category === "food" ||
      item.category === "coffee" ||
      item.category === "attraction") &&
    item.status !== "confirmed"
  );
}

function SortableRow({
  item,
  date,
  index,
  store,
  travelers,
  allPins,
  dayPins,
  voterId,
  onMoveWithin,
  hop,
  visit,
  onSelectItem,
  canMoveUp,
  canMoveDown,
}: {
  item: ScheduleItem;
  date: string;
  index: number;
  store: TripStore;
  travelers: Traveler[];
  allPins: TripData["pins"];
  dayPins: TripData["pins"];
  voterId: TravelerId;
  onMoveWithin: (itemId: string, toIndex: number) => void;
  hop: string | null;
  visit: VisitState;
  onSelectItem?: (id: string) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const canDrag = isDraggable(item);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !canDrag });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };

  const photoPin = findPinForScheduleItem(item, dayPins, allPins);
  const showPlace =
    !!photoPin &&
    pinMayHavePhoto(photoPin) &&
    (item.category === "attraction" ||
      item.category === "food" ||
      item.category === "coffee" ||
      item.category === "lodging" ||
      item.category === "trek");

  const showVotes =
    item.category === "attraction" ||
    item.category === "food" ||
    item.category === "coffee" ||
    item.status === "proposed";

  const hours = formatEventHours(item.time, item.endTime);
  const isCoffee = item.category === "coffee";
  const travelerChips = chipsForScheduleItem(item, travelers);
  const solo = soloTravelerForItem(item, travelers);
  const [notesOpen, setNotesOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const moveWithin = (toIndex: number) => {
    if (!canDrag) return;
    onMoveWithin(item.id, toIndex);
  };

  const notes = item.detail?.trim() ?? "";
  const notesLong = notes.length > 140;
  const notesShown =
    !notesLong || notesOpen ? notes : `${notes.slice(0, 140).trim()}…`;

  return (
    <li
      ref={setNodeRef}
      style={style}
      onClick={() => onSelectItem?.(item.id)}
      aria-current={visit === "active" ? "step" : undefined}
      className={[
        "schedule-item",
        "feed-capable",
        `status-${item.status ?? "confirmed"}`,
        isDragging ? "is-dragging" : "",
        canDrag ? "" : "is-locked",
        isCoffee ? "is-coffee" : "",
        visit === "active" ? "is-quest-active" : "",
        visit === "visited" ? "is-quest-done" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="feed-caption">
        <time
          className={`sched-time${hours.isTbd ? " is-tbd" : ""}`}
          dateTime={hours.isTbd ? undefined : item.time}
        >
          {hours.label}
        </time>
        {solo && (
          <span
            className={`solo-avatar solo-${solo.id}`}
            title={solo.shortName}
            aria-label={solo.shortName}
          >
            {soloInitial(solo)}
          </span>
        )}
        <span className="feed-status-chip">
          <StatusBadge status={item.status} />
        </span>
      </div>
      <div className="sched-main">
        <div className="sched-left">
          {canDrag ? (
            <button
              type="button"
              className="drag-handle"
              aria-label={`Drag ${item.title}`}
              title="Drag to reorder"
              {...attributes}
              {...listeners}
            >
              ⋮⋮
            </button>
          ) : (
            <span
              className="drag-locked"
              title="Locked — cannot reorder"
              aria-label="Locked item"
            >
              🔒
            </span>
          )}
          <CategoryIcon category={item.category} />
        </div>
        <div className="sched-body">
          <div className="sched-title-row">
            {visit === "active" && (
              <span className="quest-mark" aria-label="Active quest">
                ✦
              </span>
            )}
            {visit === "visited" && (
              <span className="quest-stamp" aria-label="Visited">
                ✓
              </span>
            )}
            <strong>{item.title}</strong>
            {item.questLabel && (
              <span className="quest-label">{item.questLabel}</span>
            )}
            <span className="desktop-status-chip">
              <StatusBadge status={item.status} />
            </span>
          </div>
          {hop && <p className="hop-line">{hop}</p>}
          {notes && (
            <p className="feed-notes">
              {notesShown}{" "}
              {notesLong && (
                <button
                  type="button"
                  className="feed-notes-more"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNotesOpen((v) => !v);
                  }}
                >
                  {notesOpen ? "Less" : "More"}
                </button>
              )}
            </p>
          )}
          {showPlace && photoPin && (
            <>
              <div className="desktop-place-detail">
                <PlaceDetail pin={photoPin} compact />
              </div>
              <FeedPhotoStrip
                pin={photoPin}
                onOpenDetail={() => setDetailOpen(true)}
              />
            </>
          )}
          {detailOpen && showPlace && photoPin && (
            <div
              className="feed-detail-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <PlaceDetail pin={photoPin} />
            </div>
          )}
          {travelerChips.length > 0 && (
            <div className="traveler-chip-row" aria-label="Travelers">
              {travelerChips.map((t) => (
                <span key={t.id} className="item-traveler-chip">
                  {t.shortName}
                </span>
              ))}
            </div>
          )}
          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noreferrer"
              className="inline-link"
              onClick={(e) => e.stopPropagation()}
            >
              Open link
            </a>
          )}
          {canDrag && (
            <div className="reorder-controls" aria-label="Reorder controls">
              <button
                type="button"
                className="btn ghost sm reorder-nudge"
                disabled={!canMoveUp}
                title={
                  canMoveUp
                    ? "Move up within this period"
                    : "Already first in this period"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canMoveUp) return;
                  moveWithin(index - 1);
                }}
                aria-label="Move up within period"
              >
                ↑
              </button>
              <button
                type="button"
                className="btn ghost sm reorder-nudge"
                disabled={!canMoveDown}
                title={
                  canMoveDown
                    ? "Move down within this period"
                    : "Already last in this period"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canMoveDown) return;
                  moveWithin(index + 1);
                }}
                aria-label="Move down within period"
              >
                ↓
              </button>
              <label className="move-day-label">
                Move to
                <select
                  value={date}
                  aria-label={`Move ${item.title} to another day`}
                  onChange={(e) => {
                    const to = e.target.value;
                    if (to !== date) store.moveItem(date, to, item.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {store.data.days.map((d) => (
                    <option key={d.date} value={d.date}>
                      {d.date.slice(5)} · {d.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      </div>
      <div className="feed-footer sched-votes">
        {showVotes && (
          <VoteButtons
            compact
            voterId={voterId}
            targetId={item.id}
            tally={store.data.votes[item.id]}
            onVote={store.setVote}
          />
        )}
        <button
          type="button"
          className="feed-discuss"
          disabled
          title="Coming soon"
          aria-disabled="true"
          onClick={(e) => e.stopPropagation()}
        >
          Discuss · Soon
        </button>
        {showPlace && photoPin && (
          <button
            type="button"
            className="feed-info"
            aria-label={`Details for ${item.title}`}
            onClick={(e) => {
              e.stopPropagation();
              setDetailOpen((v) => !v);
            }}
          >
            i
          </button>
        )}
      </div>
    </li>
  );
}

export function SortableSchedule({
  date,
  items,
  store,
  travelers,
  allPins,
  dayPins,
  voterId,
  selectedItemId = null,
  onSelectItem,
}: Props) {
  const ordered = sortScheduleItems(items);
  const visits = itemVisitStates(
    ordered.map((item) => item.id),
    selectedItemId,
  );
  const hopStops = ordered.map((item) => {
    const pin = findPinForScheduleItem(item, dayPins, allPins);
    if (!pin || !Number.isFinite(pin.lat) || !Number.isFinite(pin.lng)) return null;
    return { lat: pin.lat, lng: pin.lng, name: pin.name } satisfies LatLng & {
      name: string;
    };
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 220, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeItem = ordered.find((i) => i.id === String(active.id));
    const overItem = ordered.find((i) => i.id === String(over.id));
    if (!activeItem || !overItem || !isDraggable(activeItem)) return;
    if (!sameSchedulePeriod(activeItem.time, overItem.time)) return;
    const ids = ordered.map((i) => i.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    store.reorderDayItems(date, arrayMove(ids, oldIndex, newIndex));
  };

  const moveDisplayed = (itemId: string, toIndex: number) => {
    const ids = ordered.map((i) => i.id);
    const from = ids.indexOf(itemId);
    if (from < 0 || toIndex === from) return;
    const clamped = Math.max(0, Math.min(toIndex, ids.length - 1));
    const moving = ordered[from];
    const target = ordered[clamped];
    if (!moving || !target || !sameSchedulePeriod(moving.time, target.time)) {
      return;
    }
    store.reorderDayItems(date, arrayMove(ids, from, clamped));
  };

  if (ordered.length === 0) {
    return (
      <ul className="schedule-list">
        <li className="muted">No items yet.</li>
      </ul>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={ordered.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="schedule-list sortable-schedule">
          {ordered.map((item, index) => {
            const period = scheduleSortKey(item.time)?.period;
            const prevPeriod =
              index > 0
                ? scheduleSortKey(ordered[index - 1].time)?.period
                : undefined;
            const showPeriod =
              !!period && period !== prevPeriod;
            const weekday = format(parseISO(date), "EEEE");
            return (
              <Fragment key={item.id}>
                {showPeriod && (
                  <li className="feed-period-header">
                    {periodLabel(weekday, period as SchedulePeriod)}
                  </li>
                )}
                <SortableRow
                  item={item}
                  date={date}
                  index={index}
                  store={store}
                  travelers={travelers}
                  allPins={allPins}
                  dayPins={dayPins}
                  voterId={voterId}
                  onMoveWithin={moveDisplayed}
                  hop={hopLineAt(hopStops, index)}
                  visit={visits.byId.get(item.id) ?? "upcoming"}
                  onSelectItem={onSelectItem}
                  canMoveUp={
                    index > 0 &&
                    sameSchedulePeriod(item.time, ordered[index - 1].time)
                  }
                  canMoveDown={
                    index < ordered.length - 1 &&
                    sameSchedulePeriod(item.time, ordered[index + 1].time)
                  }
                />
              </Fragment>
            );
          })}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
